import { StatusPill } from '../ui/StatusPill';
import { EvidenceChecklistCard } from './EvidenceChecklistCard';
import {
  UNAVAILABLE,
  displayValue,
  evidenceCreationStatus,
  evidenceBundleLabel,
  fileIntegrityStatus,
  packageAdmission,
  providerEvidenceCheckCount,
  resolveEvidenceBundleId,
  custodianEvidenceChecklistItems,
} from '../../pages/custodian/custodianReviewLabels';

function Fact({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-eorig-border py-2 last:border-b-0">
      <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
        {label}
      </dt>
      <dd className="min-w-0 break-all text-right text-sm text-eorig-ink">{children}</dd>
    </div>
  );
}

export function CustodianSourceEvidencePanel({
  pkg,
  admissionStatus,
  showChecklist = true,
}) {
  const files = pkg?.files || [];
  const history = pkg?.review_history || [];
  const checks = providerEvidenceCheckCount(pkg);
  const vaultAssets = admissionStatus?.vault_assets || [];
  const primaryVault = vaultAssets[0];
  const evidenceBundleId = resolveEvidenceBundleId(pkg, admissionStatus);
  const evidenceCreated = Boolean(evidenceBundleId);
  const authoritative = packageAdmission(pkg, admissionStatus) === 'AUTHORITATIVE';

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <section className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5">
        <h3 className="text-sm font-semibold text-eorig-ink">
          {authoritative ? 'Intake source' : 'Source preview + document integrity'}
        </h3>
        {authoritative ? (
          <p className="mt-1 text-xs leading-relaxed text-eorig-muted">
            Verified submitted file. The authoritative copy is{' '}
            {primaryVault?.id ? (
              <>
                vault record <span className="font-mono">{primaryVault.id}</span>.
              </>
            ) : (
              'in the vault.'
            )}
          </p>
        ) : null}
        {files.length === 0 ? (
          <p className="mt-3 text-sm text-eorig-muted">{UNAVAILABLE}</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {files.map((file) => (
              <li
                key={file.id}
                className="rounded-xl border border-eorig-border bg-eorig-surface px-3 py-3"
              >
                <dl className="space-y-2">
                  <Fact label="Source file">{displayValue(file.file_name)}</Fact>
                  <Fact label="Status">
                    <StatusPill
                      value={file.upload_status}
                      label={fileIntegrityStatus(file)}
                    />
                  </Fact>
                  <Fact label="Verified SHA-256">
                    {file.content_sha256 ? (
                      <span className="font-mono text-[11px]">{file.content_sha256}</span>
                    ) : (
                      UNAVAILABLE
                    )}
                  </Fact>
                  {file.mime_type ? <Fact label="MIME">{file.mime_type}</Fact> : null}
                  {file.size_bytes != null ? (
                    <Fact label="Size">{String(file.size_bytes)} bytes</Fact>
                  ) : null}
                  {file.storage_account ? (
                    <Fact label="Storage">
                      <StatusPill value={file.storage_account} />
                    </Fact>
                  ) : null}
                </dl>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="space-y-3">
        {showChecklist ? (
          <EvidenceChecklistCard
            items={custodianEvidenceChecklistItems(pkg, admissionStatus)}
          />
        ) : null}

        <section className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5">
          <h3 className="text-sm font-semibold text-eorig-ink">Review details</h3>
          <p className="mt-1 text-xs text-eorig-muted">
            Provider recommendation, authority, and evidence returned for this package.
          </p>
          {checks.total > 0 ? (
            <p className="mt-3 text-sm text-eorig-ink">
              {checks.satisfied} / {checks.total} provider checks satisfied. Individual
              checklist item names are not returned by the Custodian package API.
            </p>
          ) : (
            <p className="mt-3 text-sm text-eorig-muted">0 checklist items returned</p>
          )}

          <dl className="mt-4 space-y-2.5">
            <Fact label="Provider recommendation">
              {displayValue(pkg?.recommendation || pkg?.provider_decision)}
            </Fact>
            <Fact label="Provider review decision">
              {displayValue(pkg?.provider_decision)}
            </Fact>
            <Fact label="Provider rationale">
              {displayValue(pkg?.provider_rationale)}
            </Fact>
            <Fact label="Routing status">{displayValue(pkg?.task_status)}</Fact>
            <Fact label="SHA-256 verified">
              {files.some((file) => file.content_sha256)
                ? 'Yes — backend-confirmed content_sha256'
                : UNAVAILABLE}
            </Fact>
            <Fact label="Authority decision">
              {displayValue(pkg?.authority_status || pkg?.authority_state)}
            </Fact>
            <Fact label="Evidence status">{evidenceCreationStatus(pkg, admissionStatus)}</Fact>
            <Fact label="Evidence bundle">
              {evidenceCreated ? (
                <span className="font-mono text-[11px]">{evidenceBundleId}</span>
              ) : (
                evidenceBundleLabel(pkg)
              )}
            </Fact>
            {history.length > 0 ? (
              <Fact label="Review history">
                {history.length} event{history.length === 1 ? '' : 's'} returned
              </Fact>
            ) : (
              <Fact label="Review history">{UNAVAILABLE}</Fact>
            )}
          </dl>
        </section>
      </div>
    </div>
  );
}
